import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CellContext, ExpandedState, Row } from '@tanstack/react-table';

import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';
import { NetworkPerformanceMetricDrillKey, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';

import { buildEntityCell, subRowStyle, TableWrapper } from '../../shared/slideout/expandableRowStyles';
import NetworkPerformanceApiExport from '../NetworkPerformanceApiExport';
import { useNetworkPerformanceContext } from '../NetworkPerformanceContainer';

import { getBreakdownColumnConfigs } from './breakdownColumnConfigs';
import { getQueryParams, METRIC_CONFIGS, NetworkPerformanceMetrics } from './constants';
import {
  buildNetworkPerformanceBreakdownColumns,
  buildNetworkPerformanceBreakdownRows,
  getDefaultSort,
  getSubRows,
  NetworkPerformanceBreakdownRow,
} from './networkPerformanceBreakdownColumns';

type Props = {
  metric: NetworkPerformanceMetricDrillKey;
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

export default function NetworkPerformanceBreakdownTable({ metric, loading, rollups }: Props) {
  const { predecessorGroupType } = useNetworkPerformanceContext();
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const entityHeader =
    metric === NetworkPerformanceMetrics.ENGAGEMENTS
      ? predecessorGroupType!.displayName
      : `${selectedGroupType!.displayName} Company`;

  const columnConfig = useMemo(
    () => getBreakdownColumnConfigs(selectedGroupType!.displayName)[metric],
    [metric, selectedGroupType]
  );

  const entityCell = useCallback(
    (info: CellContext<NetworkPerformanceBreakdownRow, string>) => buildEntityCell(info),
    []
  );
  const rowStyle = useCallback((row: Row<NetworkPerformanceBreakdownRow>) => subRowStyle(row), []);

  const columns = useMemo(
    () => buildNetworkPerformanceBreakdownColumns(metric, entityHeader, entityCell, columnConfig),
    [metric, entityHeader, entityCell, columnConfig]
  );
  const data = useMemo(
    () => buildNetworkPerformanceBreakdownRows(metric, rollups, columnConfig),
    [metric, rollups, columnConfig]
  );
  const defaultSort = useMemo(() => [getDefaultSort(columnConfig)], [columnConfig]);

  const config = METRIC_CONFIGS[metric];
  const exportParams = getQueryParams(metric).exportParams;
  const exportFilename = `post-discharge-${config.exportSlug}`;

  return (
    <Wrapper>
      <TitleRow>
        <TableTitle>{entityHeader} Breakdown</TableTitle>
        <NetworkPerformanceApiExport params={exportParams} filename={exportFilename} disabled={loading} />
      </TitleRow>
      <TableWrapper>
        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          freezeFirstColumn
          hasFooter={false}
          defaultSortBy={defaultSort}
          getSubRows={getSubRows}
          expanded={expanded}
          onExpandedChange={setExpanded}
          rowStyle={rowStyle}
        />
      </TableWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const TableTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: ${({ theme }) => theme.colors.black100};
`;
